import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import type { InjectedNaptime } from 'bundles/naptimejs';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import BenefitsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PortfolioProjectBenefitsModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ThinBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ThinBannerModuleRenderer';
import EnterpriseBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/EnterpriseCourseBreadcrumbsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/EnterpriseProjectProductOverviewModuleRenderer';
import ProjectContentModuleRenderer from 'bundles/unified-description-page-common/module-renderers/project-content/PortfolioProjectContentModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/EnterpriseUpsellModuleRenderer';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
  UpsellCardNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';

import ProjectsHeroImage from 'images/unified-description-page/projects-hero-image.png';

type Props = {
  pageData: EnterpriseCoursePageFieldsFragment;
  pageConfig?: PageConfig | null;
  naptime: InjectedNaptime;
};

const Renderer = ({ pageData, pageConfig, naptime }: Props) => {
  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                // visibility of hero language info depends on XDP product AI dubbing, user site language
                showHeroLanguageInfo:
                  pageData.dubbedLanguages?.length && getAudienceLanguageCode(pageData.dubbedLanguages),
                showBackgroundImage: true,
                backgroundImage: ProjectsHeroImage,
                hideInstructors: true,
                hideCourseraPlus: true,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ProductType },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCaseStudy,
                  ProductDetailItemNames.RecentlyUpdated,
                  ProductDetailItemNames.Exclusive,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.InstallationRequirements,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return <ProjectContentModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{
              upsellCards: [
                UpsellCardNames.CourseraPlusUpsellCard,
                UpsellCardNames.DegreeUpsellCard,
                UpsellCardNames.EnterpriseUpsellCard,
              ],
            }}
          />
        );
      case ModuleNames.ThinBanner:
        return <ThinBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return <EnterpriseBreadcrumbsModuleRenderer pageData={pageData} key={moduleConfig.name} naptime={naptime} />;
      case ModuleNames.Benefits:
        return <BenefitsModuleRenderer key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
